import { useCallback, useEffect, useInsertionEffect, useRef, useState } from 'react';
import { FileService, type FileData } from '@/shared/storage/FileService';

// Keys are original service results, never a URL string or a spread copy.
const leases = new WeakMap<FileData, { count: number }>();
export function retainOwnedFilePreviews(files: readonly FileData[]): () => void {
  const originals = [...new Set(files)];
  for (const file of originals) {
    const lease = leases.get(file) || { count: 0 };
    lease.count += 1;
    leases.set(file, lease);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const file of originals) {
      const lease = leases.get(file)!;
      if (--lease.count === 0) {
        leases.delete(file);
        FileService.disposeMediaPreviews([file]);
      }
    }
  };
}

// External props are borrowed. Only join an explicitly registered owner.
export function borrowFilePreview(file: FileData): () => void {
  return leases.has(file) ? retainOwnedFilePreviews([file]) : () => undefined;
}

export function useOwnedFilePreviews() {
  const [files, setFiles] = useState<FileData[]>([]);
  const requested = useRef(files);
  const owned = useRef(new Map<FileData, () => void>());
  const mounted = useRef(false);
  const adopt = useCallback((next: readonly FileData[]) => {
    for (const file of next) {
      if (!owned.current.has(file)) owned.current.set(file, retainOwnedFilePreviews([file]));
    }
  }, []);
  const replace = useCallback((next: FileData[]) => {
    if (!mounted.current) { retainOwnedFilePreviews(next)(); return; }
    requested.current = next;
    adopt(next); // Capture even results superseded before React commits.
    setFiles(next);
  }, [adopt]);
  useInsertionEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    const current = owned.current;
    return () => {
      // Strict Mode replays passive effects while the host remains mounted.
      // Insertion cleanup marks a real unmount; only then may ownership end.
      if (mounted.current) return;
      current.forEach(release => release());
      current.clear();
    };
  }, []);
  useEffect(() => {
    if (files !== requested.current) return;
    const retained = new Set(files);
    owned.current.forEach((release, file) => {
      if (!retained.has(file)) { release(); owned.current.delete(file); }
    });
  }, [files]); // Passive effect: old rendered borrowers have detached at commit.
  return [files, replace, adopt] as const;
}
